// =====================================================================
// Dimensions.jsx — M-009 Dimensions & poids UM (20-08-2026)
// =====================================================================
// Écran unique, mobile-first (PDA scanner) :
//   - champ scan (SSCC / barcode) autofocus, Enter → poids
//   - anti-doublon : code déjà mesuré → charge la mesure pour correction
//   - « garder les dimensions » : cartons identiques en série
//   - liste des saisies + statuts (à exporter / exporté / erreur)
//   - admin : exporter maintenant + aperçu fichier + état SFTP
//   - palettes (23-10-2026, clients avec l'option « étiquettes + BL palettes ») : étiquette Zebra imprimée à
//     l'enregistrement, ID créé par Zeus si la palette n'a pas d'étiquette, BL « détail des palettes » (Konica)
// =====================================================================

import { useState, useEffect, useRef, useCallback } from 'react';
import { umMeasures as umApi, errMsg } from '../services/api.js';
import { useAuth } from '../hooks/useAuth.jsx';
import Breadcrumbs from '../components/Breadcrumbs.jsx';
import { kgDeGrammes, erreurPaletteEcran, etatImpression, messageEtiquette } from './dimensions-palettes.js';

const STATUS_BADGE = {
  A_EXPORTER: { label: 'à exporter', bg: '#FEF3C7', fg: '#92400E' },
  EXPORTE:    { label: 'exporté',    bg: '#D1FAE5', fg: '#065F46' },
  ERREUR:     { label: 'erreur',     bg: '#FEE2E2', fg: '#991B1B' },
  HORS_EFT:   { label: 'pas pour EffiTrace (ID Zeus)', bg: '#F1F5F9', fg: '#475569' },
};
const petitBouton = { border: 0, background: 'transparent', cursor: 'pointer', fontSize: '0.85rem', padding: '0 2px' };

const inp = {
  width: '100%', padding: '0.6rem 0.6rem', border: '1px solid #d1d5db',
  borderRadius: 6, fontSize: '1rem', background: '#fff', boxSizing: 'border-box',
};

export default function Dimensions() {
  const { can } = useAuth();
  const isAdmin = can('um_dimensions.admin.all');

  const [codeType, setCodeType] = useState('sscc');        // sscc | barcode
  const [code, setCode] = useState('');
  const [orderRef, setOrderRef] = useState('');             // n° commande (mail uniquement — PAS EffiTrace)
  const [clientId, setClientId] = useState('');             // destinataire mail
  const [clients, setClients] = useState([]);
  const [weightG, setWeightG] = useState('');
  const [dims, setDims] = useState({ l: '', w: '', h: '' });
  const [keepDims, setKeepDims] = useState(true);
  const [photos, setPhotos] = useState([]);                // File[] — uploadées après la sauvegarde
  const [editId, setEditId] = useState(null);              // correction d'une mesure existante
  const [editHorsEft, setEditHorsEft] = useState(false);   // … d'une palette à ID Zeus (jamais exportée)
  const [flash, setFlash] = useState(null);                // message de confirmation
  const [busy, setBusy] = useState(false);
  // Palettes (23-10-2026)
  const [palette, setPalette] = useState(true);            // client avec l'option : cette mesure est une palette
  const [genererId, setGenererId] = useState(false);       // palette sans étiquette : ID créé par Zeus
  const [commande, setCommande] = useState(null);          // palettes de la commande en cours (étiquettes, BL)
  const [infoEtiquette, setInfoEtiquette] = useState(null); // { texte, url, ok } — dernière étiquette
  const demandeCommande = useRef(0);

  const [list, setList] = useState([]);
  const [flow, setFlow] = useState(null);
  const scanRef = useRef(null);
  const orderRef2 = useRef(null);
  const weightRef = useRef(null);

  const [showClients, setShowClients] = useState(false);
  const [allClients, setAllClients] = useState([]);        // catalog complet (admin — inclut inactifs)
  const [newClient, setNewClient] = useState({ name: '', emails: '' });

  const loadClients = useCallback(async () => {
    try {
      setClients(await umApi.clients());
      setAllClients(await umApi.clients(true));
    } catch (_) { /* perm */ }
  }, []);

  const refresh = useCallback(async () => {
    try {
      setList(await umApi.list({ limit: 100 }));
      setFlow(await umApi.flowStatus());
    } catch (_) { /* perm */ }
  }, []);
  useEffect(() => { refresh(); loadClients(); }, [refresh, loadClients]);

  // Palettes : seulement pour un client avec l'option ; la commande en cours se recharge quand on la change.
  const clientChoisi = clients.find((cl) => cl.id === clientId);
  const optionBl = !!clientChoisi?.blPalettes;
  const modePalette = optionBl && palette && !editId;
  const chargerCommande = useCallback(async (cid, ref) => {
    const n = ++demandeCommande.current;   // une réponse ancienne n'écrase jamais la commande affichée
    if (!cid || !String(ref || '').trim()) { setCommande(null); return; }
    try {
      const r = await umApi.palettes(cid, String(ref).trim());
      if (n === demandeCommande.current) setCommande(r);
    } catch (_) { if (n === demandeCommande.current) setCommande(null); }
  }, []);
  useEffect(() => {
    if (!optionBl) { demandeCommande.current++; setCommande(null); return undefined; }
    const t = setTimeout(() => chargerCommande(clientId, orderRef), 400);
    return () => clearTimeout(t);
  }, [optionBl, clientId, orderRef, chargerCommande]);

  // Scan validé (Enter du scanner) → anti-doublon puis focus poids
  const handleScan = async () => {
    const c = code.trim();
    if (!c) return;
    try {
      const existing = await umApi.findByCode(c);
      if (existing) {
        if (window.confirm(`Ce colis a déjà été mesuré (${existing.weightG ?? '?'} g, ${existing.lengthCm ?? '?'}×${existing.widthCm ?? '?'}×${existing.heightCm ?? '?'} cm, par ${existing.createdBy?.name || '?'}).\n\nOK = corriger les valeurs existantes · Annuler = ignorer`)) {
          setEditId(existing.id); setEditHorsEft(existing.status === 'HORS_EFT');
          setCodeType(existing.sscc === c ? 'sscc' : 'barcode');
          setOrderRef(existing.orderRef ?? '');
          setClientId(existing.clientId ?? '');
          setWeightG(existing.weightG ?? '');
          setDims({ l: existing.lengthCm ?? '', w: existing.widthCm ?? '', h: existing.heightCm ?? '' });
        } else {
          setCode(''); scanRef.current?.focus(); return;
        }
      } else {
        setEditId(null);
      }
    } catch (_) { /* réseau — on continue en création */ }
    orderRef2.current?.focus();
  };

  const resetForNext = (opts) => {
    // Palette : la commande reste (palette suivante de la même commande, puis « 🖨 BL palettes ») et la hauteur est
    // effacée (chaque palette a la sienne : jamais celle de la précédente sur une étiquette).
    const palettes = opts?.garderCommande === true;
    setCode(''); if (!palettes) setOrderRef(''); setWeightG(''); setEditId(null); setPhotos([]); setGenererId(false);
    if (!keepDims) setDims({ l: '', w: '', h: '' });
    else if (palettes) setDims((d) => ({ ...d, h: '' }));
    // après le rendu : le champ était peut-être désactivé (palette à ID Zeus), un focus immédiat serait perdu
    setTimeout(() => scanRef.current?.focus(), 0);
  };

  const handleSave = async () => {
    const c = code.trim();
    const estPalette = modePalette;
    if (estPalette) {
      const err = erreurPaletteEcran({ code: c, genererId, orderRef, weightG, heightCm: dims.h });
      if (err) { alert(err); return; }
    } else if (!c) { alert('Scannez d\'abord un SSCC ou un code-barres.'); scanRef.current?.focus(); return; }
    const idZeus = estPalette && genererId;
    const payload = {
      sscc: codeType === 'sscc' && !idZeus ? c : null,
      barcode1: codeType === 'barcode' && !idZeus ? c : null,
      orderRef: orderRef.trim() || null,
      clientId: clientId || null,
      weightG: weightG === '' ? null : Number(weightG),
      lengthCm: dims.l === '' ? null : Number(dims.l),
      widthCm: dims.w === '' ? null : Number(dims.w),
      heightCm: dims.h === '' ? null : Number(dims.h),
      ...(estPalette ? { palette: true, genererId: idZeus } : {}),
    };
    if (clientId && !orderRef.trim()) {
      alert('Pour envoyer le mail au client, scannez aussi le n° de commande.');
      orderRef2.current?.focus(); return;
    }
    setBusy(true);
    try {
      const saved = editId ? await umApi.update(editId, payload) : await umApi.create(payload);
      // Règle EffiTrace (21/08): un SSCC déjà transmis garde sa PREMIÈRE valeur là-bas
      if (saved.effitraceKept) {
        alert('⚠️ Correction enregistrée dans Zeus (et mail client renvoyé si client choisi),\nmais EffiTrace conserve la PREMIÈRE valeur transmise — corriger là-bas manuellement si nécessaire.');
      } else if (saved.effitraceIncertain) {
        alert('⚠️ Correction enregistrée dans Zeus, mais cette mesure était en cours d\'envoi à EffiTrace :\nl\'ancienne valeur est peut-être déjà partie (EffiTrace garde la PREMIÈRE valeur). Vérifiez dans EffiTrace.');
      }
      // Palette : l'étiquette part à l'enregistrement ; corrigée, Zeus propose de la réimprimer.
      if (saved.etiquette) setInfoEtiquette(messageEtiquette(saved.etiquette, saved.palette?.numero));
      else if (saved.etiquetteAReimprimer && window.confirm(`Ce qui est imprimé sur l'étiquette de la palette ${saved.palette?.numero} a changé.\n\nOK = réimprimer l'étiquette (remplacez l'ancienne sur la palette) · Annuler = ne pas réimprimer`)) {
        try { setInfoEtiquette(messageEtiquette(await umApi.imprimerEtiquette(saved.id), saved.palette?.numero)); }
        catch (eErr) { alert('Étiquette non réimprimée : ' + errMsg(eErr)); }
      }
      // Photos palettes/colis — best-effort après la sauvegarde
      for (const f of photos) {
        try { await umApi.uploadPhoto(saved.id, f); }
        catch (upErr) { alert('Mesure sauvegardée, mais échec photo : ' + errMsg(upErr)); break; }
      }
      // Mail client — APRÈS les photos (elles partent en pièces jointes)
      let mailNote = '';
      if (payload.clientId) {
        try {
          const r = await umApi.sendMail(saved.id);
          mailNote = r.sent ? ' · ✉ mail envoyé' : ' · ✉ mail en attente';
        } catch (mErr) {
          mailNote = ' · ✉ mail en erreur (réessai auto)';
        }
      }
      const quoi = saved.palette ? `palette ${saved.palette.numero} (${saved.palette.code})` : c;
      setFlash(`✓ ${quoi} ${editId ? 'corrigé' : 'enregistré'}${saved.weightG ? ` (${saved.weightG} g)` : ''}${photos.length ? ` + ${photos.length} photo(s)` : ''}${mailNote}`);
      setTimeout(() => setFlash(null), 4000);
      resetForNext({ garderCommande: !!saved.palette });
      await refresh();
      if (optionBl) chargerCommande(clientId, orderRef);
    } catch (err) {
      alert('Erreur : ' + errMsg(err));
    } finally { setBusy(false); }
  };

  // Palettes : réimpression d'une étiquette, BL de la commande en cours
  const handleEtiquette = async (id, numero) => {
    setBusy(true);
    try { setInfoEtiquette(messageEtiquette(await umApi.imprimerEtiquette(id), numero)); }
    catch (err) { alert('Erreur : ' + errMsg(err)); }
    finally { setBusy(false); refresh(); if (optionBl) chargerCommande(clientId, orderRef); }
  };
  const handleBl = async () => {
    if (!commande?.palettes?.length) return;
    if (!window.confirm(`Imprimer le BL « détail des palettes » de la commande ${commande.orderRef} (${commande.palettes.length} palette(s), ${kgDeGrammes(commande.totalG)} kg) sur la Konica ?`)) return;
    setBusy(true);
    try {
      const r = await umApi.imprimerBl(clientId, commande.orderRef);
      alert(r.travail ? `🖨 BL envoyé à la Konica (${r.palettes} palette(s)).`
        : `⚠ BL prêt mais non imprimé : ${r.message}\n\nOuvrez-le avec « 📄 Ouvrir » (à côté du bouton) pour l'imprimer à la main.`);
    } catch (err) { alert('Erreur : ' + errMsg(err)); }
    finally { setBusy(false); chargerCommande(clientId, orderRef); }
  };

  const handleEditRow = (m) => {
    const c = m.sscc || m.barcode1 || m.barcode2 || m.barcode3 || '';
    setEditId(m.id); setEditHorsEft(m.status === 'HORS_EFT'); setCode(c); setCodeType(m.sscc ? 'sscc' : 'barcode');
    setOrderRef(m.orderRef ?? ''); setClientId(m.clientId ?? '');
    setWeightG(m.weightG ?? ''); setDims({ l: m.lengthCm ?? '', w: m.widthCm ?? '', h: m.heightCm ?? '' });
    window.scrollTo({ top: 0, behavior: 'smooth' });
    weightRef.current?.focus();
  };

  const handleExportNow = async () => {
    if (!window.confirm('Déposer maintenant le fichier des mesures « à exporter » sur le SFTP EffiTrace ?')) return;
    setBusy(true);
    try {
      const r = await umApi.exportRun();
      const corr = r.corrigees ? `\n\n${r.corrigees} ligne(s) corrigée(s) pendant le dépôt : EffiTrace garde la première valeur (note ajoutée).` : '';
      const avert = r.avertissement ? `\n\n⚠ ${r.avertissement}` : '';
      alert(!r.count ? `${r.message || 'Rien à exporter'}${avert}`
        : `${r.avertissement ? '⚠' : '✓'} ${r.count} ligne(s) déposée(s) : ${r.fileName}${corr}${avert}`);
    } catch (err) {
      // Délai dépassé côté écran : l'export peut continuer sur le serveur — les pastilles diront où il en est
      // (elles se rafraîchissent seules ensuite : la page n'a pas de rafraîchissement périodique).
      const lent = /timeout/i.test(errMsg(err));
      if (lent) [30_000, 75_000, 660_000].forEach((ms) => setTimeout(() => refresh().catch(() => {}), ms));
      alert(lent
        ? 'L\'export prend du temps et continue sur le serveur. Les pastilles se mettront à jour toutes seules (dans 30 s, puis 1 min) ; un échec y sera affiché en rouge.'
        : 'Erreur : ' + errMsg(err));
    } finally { await refresh().catch(() => {}); setBusy(false); }
  };

  const handlePreview = async () => {
    try {
      const r = await umApi.exportPreview();
      alert(r.count ? `Aperçu ${r.fileName} (${r.count} ligne(s)) :\n\n${r.content}` : 'Rien à exporter.');
    } catch (err) { alert('Erreur : ' + errMsg(err)); }
  };

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '0.75rem' }}>
      <Breadcrumbs items={[{ label: 'Dimensions & poids' }]} />
      <h1 style={{ fontSize: '1.25rem', margin: '0.5rem 0 0.75rem' }}>📦 Dimensions & poids colis</h1>

      {flow && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12, fontSize: '0.8125rem' }}>
          <span style={{ background: '#FEF3C7', color: '#92400E', borderRadius: 999, padding: '2px 10px', fontWeight: 600 }}>
            {flow.pending} à exporter
          </span>
          {flow.aMarquer > 0 && (
            <span style={{ background: '#FEE2E2', color: '#991B1B', borderRadius: 999, padding: '2px 10px', fontWeight: 600 }}
              title="Déjà déposées chez EffiTrace, mais pas encore marquées « exportées » dans Zeus : elles ne repartent pas, le marquage est retenté.">
              {flow.aMarquer} déposée(s), marquage en attente
            </span>
          )}
          {flow.enDepot > 0 && (
            <span style={{ background: '#DBEAFE', color: '#1E40AF', borderRadius: 999, padding: '2px 10px', fontWeight: 600 }}
              title="Fichier en cours de dépôt sur le SFTP EffiTrace (peut durer plusieurs minutes si EffiTrace ne répond pas).">
              {flow.enDepot} en cours de dépôt
            </span>
          )}
          {flow.dernierEchec && (
            <span style={{ background: '#FEE2E2', color: '#991B1B', borderRadius: 999, padding: '2px 10px', fontWeight: 600 }}
              title={flow.dernierEchec.message}>
              ⚠ dernier export échoué ({new Date(flow.dernierEchec.at).toLocaleString('fr-BE')}) : {flow.dernierEchec.message}
            </span>
          )}
          {flow.lastExport && (
            <span style={{ color: '#6b7280' }}>
              dernier envoi : {flow.lastExport.exportFile} ({new Date(flow.lastExport.exportedAt).toLocaleString('fr-BE')})
            </span>
          )}
          {!flow.sftpConfigured && (
            <span style={{ background: '#F1F5F9', color: '#64748B', borderRadius: 999, padding: '2px 10px' }}>
              SFTP EffiTrace pas encore configuré — les saisies sont conservées
            </span>
          )}
          {isAdmin && (
            <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
              <button onClick={handlePreview} disabled={busy}
                style={{ padding: '4px 10px', fontSize: '0.75rem', background: '#fff', border: '1px solid #d1d5db', borderRadius: 5, cursor: 'pointer' }}>
                Aperçu fichier
              </button>
              <button onClick={handleExportNow} disabled={busy || !flow.sftpConfigured}
                style={{ padding: '4px 10px', fontSize: '0.75rem', background: flow.sftpConfigured ? '#2563eb' : '#94a3b8', color: '#fff', border: 0, borderRadius: 5, cursor: 'pointer' }}>
                ⬆ Exporter maintenant
              </button>
              <button onClick={() => setShowClients(!showClients)}
                style={{ padding: '4px 10px', fontSize: '0.75rem', background: '#fff', border: '1px solid #d1d5db', borderRadius: 5, cursor: 'pointer' }}>
                ⚙ Clients mail
              </button>
            </span>
          )}
        </div>
      )}

      {/* --- Gestion catalog clients mail (admin) --- */}
      {isAdmin && showClients && (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '0.9rem', marginBottom: 14 }}>
          <div style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: 8 }}>✉ Clients — adresses pour l'envoi automatique</div>
          {allClients.map((cl) => (
            <div key={cl.id} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' }}>
              <span style={{ minWidth: 140, fontWeight: 600, fontSize: '0.8125rem', color: cl.isActive ? '#0f172a' : '#94a3b8' }}>{cl.name}</span>
              <input defaultValue={cl.emails} onBlur={async (e) => {
                if (e.target.value !== cl.emails) {
                  try { await umApi.clientUpdate(cl.id, { emails: e.target.value }); loadClients(); }
                  catch (err) { alert('Erreur : ' + errMsg(err)); }
                }
              }} style={{ ...inp, flex: 1, fontSize: '0.8125rem', padding: '0.35rem 0.5rem' }} placeholder="adresse1@…, adresse2@…" />
              <label style={{ fontSize: '0.75rem', color: '#475569' }}>
                <input type="checkbox" checked={cl.isActive}
                  onChange={async (e) => { await umApi.clientUpdate(cl.id, { isActive: e.target.checked }); loadClients(); }} /> actif
              </label>
              <label style={{ fontSize: '0.75rem', color: '#475569' }}
                title="Étiquette Zebra sur chaque palette mesurée + BL « détail des palettes » (poids brut et hauteur, pour la douane)">
                <input type="checkbox" checked={!!cl.blPalettes}
                  onChange={async (e) => {
                    try { await umApi.clientUpdate(cl.id, { blPalettes: e.target.checked }); loadClients(); }
                    catch (err) { alert('Erreur : ' + errMsg(err)); }
                  }} /> 🏷 étiquettes + BL palettes
              </label>
            </div>
          ))}
          <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
            <input value={newClient.name} onChange={(e) => setNewClient({ ...newClient, name: e.target.value })}
              placeholder="Nom du client" style={{ ...inp, width: 180, fontSize: '0.8125rem', padding: '0.35rem 0.5rem' }} />
            <input value={newClient.emails} onChange={(e) => setNewClient({ ...newClient, emails: e.target.value })}
              placeholder="adresse1@…, adresse2@…" style={{ ...inp, flex: 1, fontSize: '0.8125rem', padding: '0.35rem 0.5rem' }} />
            <button onClick={async () => {
              try { await umApi.clientCreate(newClient); setNewClient({ name: '', emails: '' }); loadClients(); }
              catch (err) { alert('Erreur : ' + errMsg(err)); }
            }} style={{ padding: '4px 12px', fontSize: '0.75rem', background: '#059669', color: '#fff', border: 0, borderRadius: 5, cursor: 'pointer' }}>+ Ajouter</button>
          </div>
        </div>
      )}

      {/* --- Formulaire scan-first (PDA friendly) --- */}
      <div style={{ background: '#fff', border: editId ? '2px solid #f59e0b' : '1px solid #e2e8f0', borderRadius: 10, padding: '0.9rem', marginBottom: 14 }}>
        {editId && (
          <div style={{ fontSize: '0.8125rem', color: '#92400E', background: '#FEF3C7', borderRadius: 6, padding: '4px 10px', marginBottom: 8 }}>
            {editHorsEft
              ? '✏️ Correction d\'une palette à ID Zeus — elle reste hors du fichier EffiTrace ; si le poids ou la hauteur change, Zeus propose de réimprimer l\'étiquette.'
              : '✏️ Correction d\'une mesure existante — l\'enregistrement repartira dans le prochain export.'}
            <button onClick={resetForNext} style={{ marginLeft: 8, fontSize: '0.75rem', border: 0, background: 'transparent', color: '#92400E', textDecoration: 'underline', cursor: 'pointer' }}>annuler</button>
          </div>
        )}
        <div style={{ display: 'flex', gap: 6, marginBottom: 6 }}>
          {['sscc', 'barcode'].map((t) => (
            <button key={t} onClick={() => setCodeType(t)}
              style={{ padding: '4px 12px', fontSize: '0.75rem', fontWeight: 600, borderRadius: 999, cursor: 'pointer',
                border: '1px solid ' + (codeType === t ? '#2563eb' : '#d1d5db'),
                background: codeType === t ? '#DBEAFE' : '#fff', color: codeType === t ? '#1e40af' : '#6b7280' }}>
              {t === 'sscc' ? 'SSCC' : 'Barcode transporteur'}
            </button>
          ))}
        </div>
        <input ref={scanRef} autoFocus value={code} disabled={modePalette && genererId}
          placeholder={modePalette && genererId ? `ID créé à l'enregistrement : ${(orderRef.trim() || '<commande>').toUpperCase()}-P…` : '🔍 Scannez ou tapez le code…'}
          onChange={(e) => setCode(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleScan(); } }}
          style={{ ...inp, fontSize: '1.05rem', fontFamily: 'monospace', ...(modePalette && genererId ? { background: '#F0FDF4' } : {}) }} />
        {/* Mail client (20-08-2026) : n° commande (sujet du mail — PAS transmis à EffiTrace) + destinataire */}
        <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr', gap: 8, marginTop: 8 }}>
          <div>
            <label style={{ fontSize: '0.75rem', color: '#6b7280' }}>N° commande <span style={{ color: '#94a3b8' }}>(pour le mail client — pas EffiTrace)</span></label>
            <input ref={orderRef2} value={orderRef} placeholder="Scannez le n° de commande…"
              onChange={(e) => setOrderRef(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); weightRef.current?.focus(); } }}
              style={{ ...inp, fontFamily: 'monospace' }} />
          </div>
          <div>
            <label style={{ fontSize: '0.75rem', color: '#6b7280' }}>Client (mail auto)</label>
            <select value={clientId} onChange={(e) => setClientId(e.target.value)} style={{ ...inp, padding: '0.55rem 0.4rem' }}>
              <option value="">— pas d'envoi —</option>
              {clients.map((cl) => <option key={cl.id} value={cl.id}>{cl.name}</option>)}
            </select>
          </div>
        </div>
        {/* Palettes (23-10-2026) : client avec l'option « étiquettes + BL palettes » */}
        {optionBl && !editId && (
          <div style={{ marginTop: 8, padding: '0.5rem 0.6rem', background: '#F0FDF4', border: '1px solid #BBF7D0', borderRadius: 8, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', fontSize: '0.8125rem' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontWeight: 600, color: '#166534' }}>
              <input type="checkbox" checked={palette} onChange={(e) => { setPalette(e.target.checked); if (!e.target.checked) setGenererId(false); }} />
              🏷 Palette : étiquette Zebra + BL
            </label>
            {palette && (
              <button type="button" onClick={() => { setGenererId(!genererId); setCode(''); }}
                style={{ padding: '4px 10px', fontSize: '0.75rem', fontWeight: 600, borderRadius: 6, cursor: 'pointer',
                  border: '1px solid ' + (genererId ? '#16a34a' : '#d1d5db'), background: genererId ? '#DCFCE7' : '#fff', color: genererId ? '#166534' : '#374151' }}>
                {genererId ? '✓ ID créé par Zeus — annuler' : '🏷 Pas d\'étiquette ? Créer un ID palette'}
              </button>
            )}
            {palette && <span style={{ color: '#64748B' }}>poids et hauteur obligatoires</span>}
          </div>
        )}
        <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr 1fr 1fr', gap: 8, marginTop: 8 }}>
          <div>
            <label style={{ fontSize: '0.75rem', color: '#6b7280' }}>Poids (g)</label>
            <input ref={weightRef} type="number" min="1" inputMode="numeric" value={weightG}
              onChange={(e) => setWeightG(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSave(); } }}
              style={inp} />
          </div>
          {[['l', 'Long. (cm)'], ['w', 'Larg. (cm)'], ['h', 'Haut. (cm)']].map(([k, lbl]) => (
            <div key={k}>
              <label style={{ fontSize: '0.75rem', color: '#6b7280' }}>{lbl}</label>
              <input type="number" min="1" inputMode="numeric" value={dims[k]}
                onChange={(e) => setDims({ ...dims, [k]: e.target.value })}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSave(); } }}
                style={inp} />
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 10, flexWrap: 'wrap' }}>
          <button onClick={handleSave} disabled={busy}
            style={{ flex: '1 1 200px', padding: '0.7rem', fontSize: '1rem', fontWeight: 700, background: '#059669', color: '#fff', border: 0, borderRadius: 8, cursor: 'pointer' }}>
            ✓ {editId ? 'Corriger' : 'Enregistrer'}
          </button>
          <label style={{ fontSize: '0.875rem', color: '#2563eb', border: '1px dashed #93c5fd', borderRadius: 8, padding: '0.55rem 0.9rem', cursor: 'pointer', background: '#EFF6FF' }}>
            📷 Photo{photos.length ? ` (${photos.length})` : ''}
            <input type="file" accept="image/*" capture="environment" multiple style={{ display: 'none' }}
              onChange={(e) => { setPhotos([...photos, ...Array.from(e.target.files || [])]); e.target.value = ''; }} />
          </label>
          <label style={{ fontSize: '0.8125rem', color: '#475569', display: 'flex', alignItems: 'center', gap: 5 }}>
            <input type="checkbox" checked={keepDims} onChange={(e) => setKeepDims(e.target.checked)} />
            garder les dimensions (cartons identiques)
          </label>
        </div>
        {photos.length > 0 && (
          <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
            {photos.map((f, i) => (
              <span key={i} style={{ fontSize: '0.75rem', background: '#F1F5F9', borderRadius: 6, padding: '3px 8px', color: '#475569' }}>
                📷 {f.name.slice(0, 18)}{f.name.length > 18 ? '…' : ''}
                <button onClick={() => setPhotos(photos.filter((_, j) => j !== i))}
                  style={{ border: 0, background: 'transparent', cursor: 'pointer', color: '#94a3b8', marginLeft: 4 }}>✕</button>
              </span>
            ))}
          </div>
        )}
        {flash && <div style={{ marginTop: 8, fontSize: '0.875rem', color: '#065F46', fontWeight: 600 }}>{flash}</div>}
        {infoEtiquette && (
          <div style={{ marginTop: 6, fontSize: '0.8125rem', fontWeight: 600, color: infoEtiquette.ok ? '#065F46' : '#92400E' }}>
            {infoEtiquette.texte}
            {infoEtiquette.url && <> <a href={infoEtiquette.url} target="_blank" rel="noreferrer">📄 Ouvrir</a></>}
          </div>
        )}
      </div>

      {/* --- Palettes de la commande en cours : étiquettes, BL « détail des palettes » --- */}
      {optionBl && commande?.orderRef && (
        <div style={{ background: '#fff', border: '1px solid #BBF7D0', borderRadius: 10, padding: '0.8rem 0.9rem', marginBottom: 14 }}>
          <div style={{ fontSize: '0.875rem', fontWeight: 700, color: '#166534', marginBottom: 6 }}>
            🏷 Palettes — commande {commande.orderRef} · {clientChoisi?.name}
          </div>
          {!commande.palettes.length ? (
            <div style={{ fontSize: '0.8125rem', color: '#64748B', marginBottom: 6 }}>Aucune palette mesurée pour cette commande.</div>
          ) : (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
              {commande.palettes.map((p) => {
                const e = etatImpression(p.etiquette?.impression);
                return (
                  <span key={p.umMeasureId} title={p.code}
                    style={{ fontSize: '0.75rem', background: '#F8FAFC', border: '1px solid #e2e8f0', borderRadius: 8, padding: '3px 8px', display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                    <strong>Palette {p.numero}</strong> · {kgDeGrammes(p.weightG)} kg · {p.heightCm ?? '?'} cm
                    <span style={{ color: e.ok ? '#065F46' : '#991B1B' }}>{e.texte}</span>
                    <button onClick={() => handleEtiquette(p.umMeasureId, p.numero)} disabled={busy} title="Réimprimer l'étiquette" style={petitBouton}>↻</button>
                    {p.etiquette?.document && <a href={p.etiquette.document.url} target="_blank" rel="noreferrer" title="Ouvrir l'étiquette" style={{ textDecoration: 'none' }}>📄</a>}
                  </span>
                );
              })}
            </div>
          )}
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.8125rem', color: '#334155' }}>Total : {commande.palettes.length} palette(s) · {kgDeGrammes(commande.totalG)} kg</span>
            <button onClick={handleBl} disabled={busy || !commande.palettes.length}
              style={{ padding: '0.5rem 0.9rem', fontSize: '0.875rem', fontWeight: 700, background: commande.palettes.length ? '#2563eb' : '#94a3b8', color: '#fff', border: 0, borderRadius: 8, cursor: commande.palettes.length ? 'pointer' : 'default' }}>
              🖨 Imprimer le BL palettes
            </button>
            {commande.dernierBl && (
              <span style={{ fontSize: '0.75rem', color: '#64748B' }}>
                dernier BL : {new Date(commande.dernierBl.document.createdAt).toLocaleString('fr-BE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}
                {' · '}{etatImpression(commande.dernierBl.impression).texte}
                {' '}<a href={commande.dernierBl.document.url} target="_blank" rel="noreferrer">📄 Ouvrir</a>
              </span>
            )}
          </div>
          {!commande.agent?.disponible && commande.agent?.message && (
            <div style={{ marginTop: 6, fontSize: '0.75rem', color: '#92400E' }}>⚠ {commande.agent.message}</div>
          )}
        </div>
      )}

      {/* --- Liste --- */}
      <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
            <thead style={{ background: '#F8FAFC' }}>
              <tr>
                {['SSCC / barcode', 'Commande', 'Poids', 'L×l×H (cm)', 'Par', 'Quand', 'Statut', 'Mail', ''].map((h) => (
                  <th key={h} style={{ padding: '6px 8px', textAlign: 'left', color: '#64748B', fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {list.map((m) => {
                // Déjà déposée chez EffiTrace (ou en cours de dépôt), pas encore marquée « exportée ».
                const b = m.deposeeAMarquer ? { label: 'déposée · marquage en attente', bg: '#FEE2E2', fg: '#991B1B' }
                  : m.depotEnCours ? { label: 'dépôt en cours', bg: '#E0E7FF', fg: '#3730A3' }
                  : STATUS_BADGE[m.status] || STATUS_BADGE.A_EXPORTER;
                return (
                  <tr key={m.id} style={{ borderTop: '1px solid #F1F5F9' }}>
                    <td style={{ padding: '5px 8px', fontFamily: 'monospace' }}>
                      {m.sscc || m.barcode1 || m.barcode2 || m.barcode3}
                      {m.palette && (
                        <span title={`Palette ${m.palette.numero} de la commande ${m.palette.orderRef} — étiquette : ${etatImpression(m.palette.etiquette?.impression).texte}`}
                          style={{ marginLeft: 6, fontFamily: 'inherit', fontSize: '0.75rem', fontWeight: 700, color: '#166534', background: '#DCFCE7', borderRadius: 999, padding: '1px 7px' }}>
                          🏷 {m.palette.numero}
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '5px 8px', fontFamily: 'monospace' }}>{m.orderRef || '—'}</td>
                    <td style={{ padding: '5px 8px' }}>{m.weightG ? `${m.weightG} g` : '—'}</td>
                    <td style={{ padding: '5px 8px' }}>{[m.lengthCm, m.widthCm, m.heightCm].some((v) => v) ? `${m.lengthCm ?? '?'}×${m.widthCm ?? '?'}×${m.heightCm ?? '?'}` : '—'}</td>
                    <td style={{ padding: '5px 8px' }}>
                      {m.createdBy?.name || '—'}
                      {m.photos?.length > 0 && (
                        <a href={umApi.photoUrl(m.id, m.photos[0].id)} target="_blank" rel="noreferrer"
                          title={`${m.photos.length} photo(s) — ouvrir`} style={{ marginLeft: 6, textDecoration: 'none' }}>
                          📷{m.photos.length > 1 ? m.photos.length : ''}
                        </a>
                      )}
                    </td>
                    <td style={{ padding: '5px 8px', color: '#6b7280' }}>{new Date(m.createdAt).toLocaleString('fr-BE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' })}</td>
                    <td style={{ padding: '5px 8px' }}>
                      <span title={m.errorMsg || (m.exportFile ? `fichier ${m.exportFile}` : '')}
                        style={{ background: b.bg, color: b.fg, borderRadius: 999, padding: '2px 8px', fontSize: '0.75rem', fontWeight: 600 }}>
                        {b.label}{m.status === 'ERREUR' && m.errorMsg ? ` : ${m.errorMsg}` : ''}
                      </span>
                    </td>
                    <td style={{ padding: '5px 8px' }}>
                      {m.clientId ? (
                        <span
                          title={`${m.client?.name || ''}${m.mailError ? ` — ${m.mailError}` : ''}${m.mailSentAt ? ` — ${new Date(m.mailSentAt).toLocaleString('fr-BE')}` : ''}`}
                          style={{
                            borderRadius: 999, padding: '2px 8px', fontSize: '0.75rem', fontWeight: 600,
                            background: m.mailStatus === 'ENVOYE' ? '#D1FAE5' : m.mailStatus === 'ERREUR' ? '#FEE2E2' : '#F1F5F9',
                            color: m.mailStatus === 'ENVOYE' ? '#065F46' : m.mailStatus === 'ERREUR' ? '#991B1B' : '#475569',
                          }}>
                          ✉ {m.mailStatus === 'ENVOYE' ? 'envoyé' : m.mailStatus === 'ERREUR' ? 'erreur' : 'en attente'}
                        </span>
                      ) : <span style={{ color: '#cbd5e1' }}>—</span>}
                      {m.clientId && m.mailStatus !== 'ENVOYE' && (
                        <button onClick={async () => { try { await umApi.sendMail(m.id); } catch (_) { /* marqué */ } refresh(); }}
                          title="Renvoyer" style={{ border: 0, background: 'transparent', cursor: 'pointer', fontSize: '0.85rem' }}>🔄</button>
                      )}
                    </td>
                    <td style={{ padding: '5px 8px' }}>
                      <button onClick={() => handleEditRow(m)} title="Corriger"
                        style={{ border: 0, background: 'transparent', cursor: 'pointer', fontSize: '0.9rem' }}>✏️</button>
                      {m.palette && (
                        <button onClick={() => handleEtiquette(m.id, m.palette.numero)} disabled={busy} title="Réimprimer l'étiquette de la palette"
                          style={{ border: 0, background: 'transparent', cursor: 'pointer', fontSize: '0.9rem' }}>🏷↻</button>
                      )}
                      {m.palette?.etiquette?.document && (
                        <a href={m.palette.etiquette.document.url} target="_blank" rel="noreferrer" title="Ouvrir l'étiquette" style={{ textDecoration: 'none', fontSize: '0.9rem' }}>📄</a>
                      )}
                      {isAdmin && (
                        <button onClick={async () => { if (window.confirm('Supprimer cette mesure ?')) { await umApi.remove(m.id); refresh(); } }}
                          title="Supprimer" style={{ border: 0, background: 'transparent', cursor: 'pointer', fontSize: '0.9rem' }}>🗑️</button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {!list.length && (
                <tr><td colSpan={9} style={{ padding: '1.5rem', textAlign: 'center', color: '#94a3b8' }}>Aucune mesure — scannez un premier colis.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
